import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { fetchAMCs, deleteAMC, clearError } from '../../store/amcSlice';
import AMCForm from './AMCForm';
import amcService from '../../services/amcService';

const AMCList = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const { list, loading, error, pagination } = useSelector((state) => state.amc);
  const { user } = useSelector((state) => state.auth);
  const isAdmin = user?.userType === 1;
  
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedAMC, setSelectedAMC] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [amcToDelete, setAmcToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuAMC, setMenuAMC] = useState(null);

  useEffect(() => {
    dispatch(fetchAMCs({ page: 1, limit: 10, search: searchTerm }));
  }, [dispatch, searchTerm]);

  useEffect(() => {
    if (error) {
      setSnackbar({ open: true, message: error, severity: 'error' });
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleAddAMC = () => {
    setSelectedAMC(null);
    setOpenDialog(true);
  };

  const handleEditAMC = (amc) => {
    setSelectedAMC(amc);
    setOpenDialog(true);
    setAnchorEl(null);
  };

  const handleDeleteClick = (amc) => {
    setAmcToDelete(amc);
    setDeleteDialogOpen(true);
    setAnchorEl(null);
  };

  const handleDeleteConfirm = async () => {
    if (amcToDelete) {
      await dispatch(deleteAMC(amcToDelete._id));
      setSnackbar({ open: true, message: 'AMC deleted successfully', severity: 'success' });
      setDeleteDialogOpen(false);
      setAmcToDelete(null);
    }
  };

  const handleGeneratePDF = async (amc) => {
    try {
      const pdfBlob = await amcService.generatePDF(amc._id);
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `AMC-${amc.clientName.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: 'PDF generated successfully', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to generate PDF', severity: 'error' });
    }
    setAnchorEl(null);
  };

  const handleMenuClick = (event, amc) => {
    setAnchorEl(event.currentTarget);
    setMenuAMC(amc);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuAMC(null);
  };

  const columns = [
    { field: 'clientName', headerName: 'College/Client Name', width: 200, flex: 1 },
    { 
      field: 'date', 
      headerName: 'Date', 
      width: 120,
      renderCell: (params) => new Date(params.value).toLocaleDateString()
    },
    {
      field: 'isAMC',
      headerName: 'Type',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'AMC' : 'Service'}
          color={params.value ? 'primary' : 'secondary'}
          size="small"
        />
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => {
        const { allCameraOK, dvrDatetimeOK, recording } = params.row;
        const allOK = allCameraOK && dvrDatetimeOK && recording;
        return (
          <Chip
            label={allOK ? 'All OK' : 'Issues'}
            color={allOK ? 'success' : 'warning'}
            size="small"
          />
        );
      }
    },
    {
      field: 'images',
      headerName: 'Images',
      width: 80,
      renderCell: (params) => (
        <Chip
          label={params.row.images?.length || 0}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => handleMenuClick(e, params.row)}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  const MobileCard = ({ amc }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {amc.clientName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {amc.isAMC ? 'AMC' : 'Service'}
            </Typography>
          </Box>
          <IconButton size="small" onClick={(e) => handleMenuClick(e, amc)}>
            <MoreVertIcon />
          </IconButton>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip
            label={amc.isAMC ? 'AMC' : 'Service'}
            color={amc.isAMC ? 'primary' : 'secondary'}
            size="small"
          />
          <Chip
            label={amc.allCameraOK && amc.dvrDatetimeOK && amc.recording ? 'All OK' : 'Issues'}
            color={amc.allCameraOK && amc.dvrDatetimeOK && amc.recording ? 'success' : 'warning'}
            size="small"
          />
          <Chip
            label={`${amc.images?.length || 0} Images`}
            size="small"
            variant="outlined"
          />
        </Box>
        
        {amc.remarks && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {amc.remarks}
          </Typography>
        )}
        
        <Typography variant="caption" color="text.secondary">
          Date: {new Date(amc.date).toLocaleDateString()}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          AMC Records
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddAMC}
          sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
        >
          Add AMC Record
        </Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Search Client Name..."
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
        </Grid>
      </Grid>

      {isMobile ? (
        <Box>
          {loading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <Card key={index} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ height: 20, bgcolor: 'grey.300', borderRadius: 1, mb: 1 }} />
                      <Box sx={{ height: 16, bgcolor: 'grey.200', borderRadius: 1, width: '60%' }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))
          ) : (
            list.map((amc) => <MobileCard key={amc._id} amc={amc} />)
          )}
        </Box>
      ) : (
        <Paper sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={list}
            columns={columns}
            loading={loading}
            pagination
            paginationMode="server"
            rowCount={pagination.total}
            pageSize={pagination.limit}
            page={pagination.page - 1}
            onPageChange={(newPage) => {
              dispatch(fetchAMCs({ 
                page: newPage + 1, 
                limit: pagination.limit, 
                search: searchTerm
              }));
            }}
            getRowId={(row) => row._id}
            disableSelectionOnClick
          />
        </Paper>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleEditAMC(menuAMC)}>
          <EditIcon sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => handleGeneratePDF(menuAMC)}>
          <PdfIcon sx={{ mr: 1 }} />
          Generate PDF
        </MenuItem>
        {isAdmin && (
          <MenuItem onClick={() => handleDeleteClick(menuAMC)} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        )}
      </Menu>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedAMC ? 'Edit AMC Record' : 'Add New AMC Record'}
        </DialogTitle>
        <DialogContent>
          <AMCForm
            amc={selectedAMC}
            onSuccess={(message) => {
              setSnackbar({ open: true, message, severity: 'success' });
              setOpenDialog(false);
            }}
            onCancel={() => setOpenDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the record for "{amcToDelete?.clientName}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AMCList;